/**
 * StoreSkuAutoSetUp: the read-only "Automatic set-up" status inside Settings,
 * Printing, Store SKUs for standard products (88a.1, D3 revised).
 *
 * There is no control here. Automatic set-up runs only while the server opts
 * in and the org's store SKU switch is on; this block says which of those is
 * true, what the last automatic set-up and the last nightly check did, and
 * how often it runs here (within about 20 minutes plus a nightly check, or
 * once a night when the 15-minute product sync is off on this server).
 *
 * Shape (DESIGN.md, mirroring the section it sits in):
 *   - A titled sub-block under the switch, its state in a pill with the words
 *     in it (emerald On, muted otherwise), so colour is never the only signal.
 *     The pill is a status region: it changes when the switch changes, which
 *     refetches this query through the section's one `productSync` refresh.
 *   - The quick answer first: why it is not running (only when it is not),
 *     then the last runs, then one paragraph saying what it does. When it is
 *     not running, that paragraph starts "When on".
 *   - Times use the shared date format (DESIGN.md section 17).
 *   - Loading: a skeleton in the loaded shape (two last-run lines, then the
 *     paragraph), with an announced label. Error: one plain line with Retry,
 *     the only button the block ever has.
 *   - Admins and Coordinators see the same block (the query needs only
 *     settings read).
 *
 * Only for a composition org: the section passes `compositionOrg`, and the
 * query is disabled otherwise, so Mini Zali's page never asks for it.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Locale } from 'date-fns/locale';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { formatDateTime, useDateFnsLocale } from '@/lib/format-dates';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionError } from '@/components/shared/SectionError';
import { StatusPill } from '@/components/shared/StatusPill';

type AutoSetUpStatus = NonNullable<RouterOutputs['productSync']['getStoreSkuAutoSetUp']['autoSetUp']>;
type AutoSetUpState = AutoSetUpStatus['state'];
type AutoSetUpPass = AutoSetUpStatus['lastPass'];

const PILL_KEY: Record<AutoSetUpState, string> = {
  on: 'storeSkus.autoSetUp.pillOn',
  off_on_server: 'storeSkus.autoSetUp.pillOffOnServer',
  store_skus_off: 'storeSkus.autoSetUp.pillWaiting',
};

/** Why it is not running, first; nothing when it is. */
const REASON_KEY: Record<AutoSetUpState, string | null> = {
  on: null,
  off_on_server: 'storeSkus.autoSetUp.offOnServer',
  store_skus_off: 'storeSkus.autoSetUp.storeSkusOff',
};

/**
 * How often it runs here: within about 20 minutes plus a nightly check while
 * the 15-minute product sync runs on this server (`fastPass`), otherwise once
 * a night; prefixed "When on" while it is not running.
 */
const TIMING_KEY = {
  fast: { running: 'storeSkus.autoSetUp.timingFast', notRunning: 'storeSkus.autoSetUp.timingFastWhenOn' },
  nightly: { running: 'storeSkus.autoSetUp.timingNightly', notRunning: 'storeSkus.autoSetUp.timingNightlyWhenOn' },
} as const;

/** The three lines a last run can read as. */
interface PassKeys {
  none: string;
  failed: string;
  done: string;
}

const LAST_PASS_KEYS: PassKeys = {
  none: 'storeSkus.autoSetUp.lastPassNone',
  failed: 'storeSkus.autoSetUp.lastPassFailed',
  done: 'storeSkus.autoSetUp.lastPass',
};

const LAST_NIGHTLY_KEYS: PassKeys = {
  none: 'storeSkus.autoSetUp.lastNightlyNone',
  failed: 'storeSkus.autoSetUp.lastNightlyFailed',
  done: 'storeSkus.autoSetUp.lastNightly',
};

/**
 * One last-run line: none yet, did not finish (never a count), or when it ran
 * with the variants it set up (`count`) and those still waiting for a file
 * (`waiting`, which only the nightly line shows).
 */
function passLine(t: TFunction, locale: Locale, keys: PassKeys, pass: AutoSetUpPass): string {
  if (!pass) return t(keys.none);
  const time = formatDateTime(pass.at, locale);
  if (pass.outcome === 'failed') return t(keys.failed, { time });
  return t(keys.done, { time, count: pass.inserted, waiting: pass.waitingForFile });
}

/** One skeleton line in a line box of the loaded text's height (`leading-5`). */
function SkeletonLine({ className }: { className: string }): JSX.Element {
  return (
    <div className="flex h-5 items-center">
      <Skeleton className={cn('h-3', className)} />
    </div>
  );
}

export function StoreSkuAutoSetUp({ compositionOrg }: { compositionOrg: boolean }): JSX.Element | null {
  const { t } = useTranslation('settings');
  const locale = useDateFnsLocale();
  const headingId = useId();
  const query = trpc.productSync.getStoreSkuAutoSetUp.useQuery(undefined, {
    enabled: compositionOrg,
    staleTime: 30_000,
  });

  if (!compositionOrg) return null;
  const data = query.data;
  // The server is the authority on the org's shape; it may disagree with a
  // cached switch read for a moment.
  if (data && (!data.compositionOrg || !data.autoSetUp)) return null;
  const status = data?.autoSetUp ?? null;

  let pill: JSX.Element | null = null;
  let content: JSX.Element;
  if (status) {
    const { state, fastPass, lastPass, lastNightly } = status;
    pill = (
      <StatusPill role="status" tone={state === 'on' ? 'on' : 'off'} srLabel={t('storeSkus.autoSetUp.title')}>
        {t(PILL_KEY[state])}
      </StatusPill>
    );
    const reasonKey = REASON_KEY[state];
    const timingKey = TIMING_KEY[fastPass ? 'fast' : 'nightly'][state === 'on' ? 'running' : 'notRunning'];
    content = (
      <div className="space-y-1.5 text-xs leading-5 text-muted-foreground">
        {reasonKey && <p>{t(reasonKey)}</p>}
        <ul className="space-y-0.5 tabular-nums">
          <li>{passLine(t, locale, LAST_PASS_KEYS, lastPass)}</li>
          <li>{passLine(t, locale, LAST_NIGHTLY_KEYS, lastNightly)}</li>
        </ul>
        <p className="max-w-prose">{t('storeSkus.autoSetUp.body', { timing: t(timingKey) })}</p>
      </div>
    );
  } else if (query.isError) {
    content = (
      <SectionError
        variant="compact"
        body={t('storeSkus.autoSetUp.loadError')}
        onRetry={() => void query.refetch()}
        retryLabel={t('storeSkus.retry')}
        retrying={query.isFetching}
      />
    );
  } else {
    // The loaded shape while it is on: two last-run lines, then the
    // paragraph (about four lines), so nothing jumps when the data lands.
    content = (
      <div className="space-y-1.5" aria-busy="true">
        <span className="sr-only" role="status">
          {t('storeSkus.autoSetUp.loading')}
        </span>
        <div className="space-y-0.5">
          <SkeletonLine className="w-72 max-w-full" />
          <SkeletonLine className="w-80 max-w-full" />
        </div>
        <div className="max-w-prose">
          <SkeletonLine className="w-full" />
          <SkeletonLine className="w-full" />
          <SkeletonLine className="w-full" />
          <SkeletonLine className="w-2/5" />
        </div>
      </div>
    );
  }

  return (
    <section aria-labelledby={headingId} className="space-y-2 border-t pt-4" data-testid="store-skus-auto-set-up">
      <div className="flex flex-wrap items-center gap-2">
        <h4 id={headingId} className="text-sm font-medium">
          {t('storeSkus.autoSetUp.title')}
        </h4>
        {pill}
      </div>
      {content}
    </section>
  );
}
